/* ============================================================
   CaseKaro Model Search v2  (assets/ck-model-search.js)

   What it does:
   1. Animated typewriter placeholder on the HEADER search field,
      cycling top-selling phone models ("Try “iPhone 16 Pro”…").
   2. Understands a full query such as
        "iphone 18 cover spider man" / "couple case for iphone 17"
      by splitting it into  PHONE MODEL + DESIGN THEME + other words,
      and shows in the dropdown:
        - Categories: theme-for-model link (/collections/<model>/<tag>), the model collection(s), and the
          all-models theme page when one exists
        - Products: Shopify's own predictive search
          (/search/suggest.json), narrowed to the model
        - "View all results" -> native /search
      Model data: assets/ck-navigation-data.json (+ master-navigation
      if present). Products: Shopify native search, no app.
   3. Enter: model only -> model collection; model + theme -> the
      filtered model collection; anything else -> native /search.
   4. On the /search results page, adds the same category links as
      chips above the results.

   Graceful degradation: if the JSON fails to load, the theme's
   native search behaviour is left untouched.

   ROLLBACK: restore the v1 file (project backup
   claude/backups/ck-model-search.2026-10-05.v1.js).
   ============================================================ */
(function () {
  'use strict';

  var script = document.currentScript;
  var ds = (script && script.dataset) || {};
  var SOURCES = [ds.navUrl, ds.masterUrl].filter(Boolean);
  var MIN_CHARS = 2;
  var MAX_RESULTS = 8;          /* model rows when only a model is typed */
  var MAX_PRODUCTS = 5;
  var STATIC_PLACEHOLDER = '';
  var BROWSE_ALL_URL = '/pages/phone-cases-by-model';
  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  var models = [];
  var seen = {};
  var aliasMap = {};
  var dataReady = false;
  var panelCount = 0;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Typed placeholder phrases — replaced by live top sellers once data loads */
  var PHRASES = ['iPhone 16 Pro', 'Samsung S24 Ultra', 'OnePlus 12R', 'Redmi Note 14', 'Vivo V60', 'Nothing Phone 2A'];

  /* Words that carry no meaning for matching */
  var STOP = {};
  ('cover covers case cases back for phone phones mobile mobiles the a an of with and in on to my new best buy ' +
   'design designs designer printed print theme themed galaxy apple pouch online wala ka ke ki')
    .split(' ').forEach(function (w) { STOP[w] = 1; });

  /* Design themes. tag = exact Shopify product tag (used by the
     collection filter ?filter.p.tag=). page = all-models theme page.
     keys = what customers type (lowercase, space separated).
     To add a theme: add one line here. */
  var DESIGNS = [
    { tag: 'Spiderman', label: 'Spider-Man', page: '/pages/spiderman-phone-cases', keys: ['spider man', 'spiderman', 'spider verse', 'spiderverse', 'spidey', 'spider', 'miles morales', 'gwen'] },
    { tag: 'Superhero', label: 'Superhero', page: '/pages/superhero-phone-cases', keys: ['superhero', 'superheroes', 'super hero', 'super heroes', 'marvel', 'avengers', 'dc', 'batman', 'iron man', 'ironman'] },
    { tag: 'Anime', label: 'Anime', page: '/pages/anime-phone-cases', keys: ['anime', 'naruto', 'manga', 'one piece', 'dragon ball', 'demon slayer', 'jujutsu'] },
    { tag: 'Couple', label: 'Couple', keys: ['couple', 'couples', 'matching', 'pair', 'love', 'valentine'] },
    { tag: 'Bikes & Cars', label: 'Bikes & Cars', page: '/pages/bike-car-phone-cases', keys: ['car', 'cars', 'bike', 'bikes', 'racing', 'supercar', 'jdm', 'porsche', 'bmw'] },
    { tag: 'Meme', label: 'Meme', page: '/pages/meme-phone-cases', keys: ['meme', 'memes', 'funny'] },
    { tag: 'Quote', label: 'Quotes', keys: ['quote', 'quotes', 'text', 'motivational'] },
    { tag: 'Music & Cinema', label: 'Music & Cinema', page: '/pages/music-cinema-phone-cases', keys: ['music', 'cinema', 'movie', 'movies', 'film', 'bollywood', 'singer'] },
    { tag: 'Girl Aesthetic', label: 'Aesthetic', page: '/pages/aesthetic-phone-cases', keys: ['aesthetic', 'girl aesthetic', 'girly', 'girls', 'girl', 'cute'] },
    { tag: 'Pinteresty', label: 'Pinteresty', keys: ['pinteresty', 'pinterest'] },
    { tag: 'Revolution', label: 'Revolution', page: '/pages/revolution-phone-cases', keys: ['revolution'] },
    { tag: 'Space Theme', label: 'Space', keys: ['space', 'astronaut', 'nasa', 'planet', 'planets'] },
    { tag: 'Cartoon', label: 'Cartoon', keys: ['cartoon', 'cartoons'] },
    { tag: 'Religious', label: 'Religious', keys: ['religious', 'god', 'spiritual', 'shiva', 'krishna', 'ganesha', 'hanuman'] },
    { tag: 'Animals', label: 'Animals', keys: ['animal', 'animals', 'cat', 'cats', 'dog', 'dogs', 'tiger', 'lion', 'panda'] },
    { tag: 'Nature', label: 'Nature', keys: ['nature', 'floral', 'flower', 'flowers', 'mountain', 'mountains'] },
    { tag: 'Abstract', label: 'Abstract', keys: ['abstract'] },
    { tag: 'Sports', label: 'Sports', keys: ['sports', 'sport', 'cricket', 'football'] },
    { tag: 'Travel', label: 'Travel', keys: ['travel', 'wanderlust'] },
    { tag: 'Zodiac Sign', label: 'Zodiac', keys: ['zodiac', 'zodiac sign', 'astrology'] },
    { tag: 'Navratri', label: 'Navratri', keys: ['navratri', 'garba'] },
    { tag: 'Geeky', label: 'Geeky', keys: ['geeky', 'geek', 'coding', 'programmer'] },
    { tag: 'Patriotic', label: 'Patriotic', keys: ['patriotic', 'india', 'army'] },
    { tag: 'Mandala', label: 'Mandala', keys: ['mandala'] },
    { tag: 'BTS', label: 'BTS', keys: ['bts', 'kpop', 'k pop'] },
    { tag: 'Web Series', label: 'Web Series', keys: ['web series', 'series'] },
    { tag: 'Celebrity', label: 'Celebrity', keys: ['celebrity', 'celebrities'] },
    { tag: 'Fitness', label: 'Fitness', keys: ['fitness', 'gym'] },
    { tag: 'Stock Market', label: 'Stock Market', keys: ['stock market', 'trading', 'trader', 'stocks'] },
    { tag: 'Pattern', label: 'Pattern', keys: ['pattern', 'patterns'] },
    { tag: 'Texture', label: 'Texture', keys: ['texture', 'marble', 'textured'] },
    { tag: 'Artwork', label: 'Artwork', keys: ['artwork', 'art'] }
  ];

  function norm(s) {
    return String(s || '').toLowerCase().replace(/\+/g, 'plus').replace(/[^a-z0-9]+/g, '');
  }

  function tokenize(s) {
    return String(s || '').toLowerCase().replace(/\+/g, ' plus ').split(/[^a-z0-9]+/).filter(Boolean);
  }

  function addAlias(key, m) {
    if (!key || key.length < 4) return;
    if (!aliasMap[key]) aliasMap[key] = m;
    var short = key.replace(/[45]g$/, '');
    if (short !== key && short.length >= 4 && !aliasMap[short]) aliasMap[short] = m;
  }

  function addModel(name, brand, url, sales) {
    if (!name || !url) return;
    var key = norm(brand) + '|' + norm(name);
    if (seen[key]) return;
    seen[key] = true;
    var m = {
      name: name,
      brand: brand || '',
      url: url,
      sales: sales || 0,
      norm: norm(name),
      normFull: norm(brand + ' ' + name)
    };
    models.push(m);
  }

  /* Aliases are built after all models are in, full names first, so a
     combined listing ("Realme 11 Pro/Pro+ 5G") never steals the name
     of a real standalone model. */
  function buildAliases() {
    models.forEach(function (m) { aliasMap[m.norm] = aliasMap[m.norm] || m; });
    models.forEach(function (m) { addAlias(m.norm, m); addAlias(m.normFull, m); });
    models.forEach(function (m) {
      if (m.name.indexOf('/') === -1) return;
      var parts = m.name.split('/');
      var first = tokenize(parts[0]);
      addAlias(norm(parts[0]), m);
      for (var i = 1; i < parts.length; i++) {
        addAlias(norm(first[0] + ' ' + parts[i]), m);
        if (first.length > 2) addAlias(norm(first.slice(0, 2).join(' ') + ' ' + parts[i]), m);
      }
    });
  }

  function ingest(data) {
    if (!data) return;
    if (Array.isArray(data.all_models)) {
      data.all_models.forEach(function (m) {
        addModel(m.model, m.brand, m.handle ? '/collections/' + m.handle : m.collection_url, m.sales_count);
      });
    } else if (Array.isArray(data.brands)) {
      data.brands.forEach(function (b) {
        (b.models || []).forEach(function (m) {
          addModel(m.model, b.brand, m.handle ? '/collections/' + m.handle : m.collection_url, m.sales_count);
        });
      });
    } else if (Array.isArray(data)) {
      data.forEach(function (m) {
        addModel(m.model || m.name, m.brand, m.url || m.collection_url, m.sales_count);
      });
    }
  }

  function loadData() {
    var jobs = SOURCES.map(function (u) {
      return fetch(u)
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    });
    return Promise.all(jobs).then(function (results) {
      results.forEach(ingest);
      buildAliases();
      dataReady = models.length > 0;
      if (dataReady) {
        var top = models.slice().sort(function (a, b) { return b.sales - a.sales; }).slice(0, 6);
        if (top.length >= 3) PHRASES = top.map(function (m) { return m.name; });
      }
    });
  }

  /* v1 matcher: whole text against model names (prefix / contains) */
  function search(q, max) {
    var nq = norm(q);
    if (nq.length < MIN_CHARS) return [];
    var scored = [];
    for (var i = 0; i < models.length; i++) {
      var m = models[i];
      var s = -1;
      if (m.norm === nq) s = 4;
      else if (m.norm.indexOf(nq) === 0) s = 3;
      else if (m.norm.indexOf(nq) > -1) s = 2;
      else if (m.normFull.indexOf(nq) > -1) s = 1;
      if (s > -1) scored.push([s, m]);
    }
    scored.sort(function (a, b) { return b[0] - a[0] || b[1].sales - a[1].sales; });
    return scored.slice(0, max || MAX_RESULTS).map(function (x) { return x[1]; });
  }

  function findDesign(tokens) {
    var hay = ' ' + tokens.join(' ') + ' ';
    var best = null, bestKey = '';
    for (var i = 0; i < DESIGNS.length; i++) {
      for (var k = 0; k < DESIGNS[i].keys.length; k++) {
        var key = DESIGNS[i].keys[k];
        if (key.length > bestKey.length && hay.indexOf(' ' + key + ' ') > -1) { best = DESIGNS[i]; bestKey = key; }
      }
    }
    if (!best) return null;
    var left = (' ' + tokens.join(' ') + ' ').replace(' ' + bestKey + ' ', ' ').trim();
    return { design: best, extra: left ? left.split(' ') : [] };
  }

  /* Split a query into model(s) + design + remaining words */
  function parse(q) {
    var all = tokenize(q);
    var tokens = all.filter(function (t) { return t !== 'galaxy' && t !== 'apple'; });
    var best = null; /* {i, len, w, exact} */
    var i, len, w, hit;

    for (i = 0; i < tokens.length; i++) {
      for (len = Math.min(6, tokens.length - i); len >= 1; len--) {
        w = tokens.slice(i, i + len).join('');
        if (w.length < 3) continue;
        hit = null;
        if (aliasMap[w]) hit = { exact: true };
        else if (partial(w, 1).length) hit = { exact: false };
        if (hit && (!best || w.length > best.w.length || (w.length === best.w.length && hit.exact && !best.exact))) {
          best = { i: i, len: len, w: w, exact: hit.exact };
        }
      }
    }

    var out = { raw: q, models: [], exact: false, design: null, rest: [], extra: [], modelText: '' };
    var restTokens = tokens;

    if (best) {
      out.exact = best.exact;
      out.modelText = tokens.slice(best.i, best.i + best.len).join(' ');
      if (best.exact) {
        var m0 = aliasMap[best.w];
        out.models = [m0].concat(partial(best.w, 4).filter(function (m) { return m !== m0; }));
      } else {
        out.models = partial(best.w, 80);
      }
      restTokens = tokens.slice(0, best.i).concat(tokens.slice(best.i + best.len));
      if (!best.exact) {
        /* "google 10": brand typed, model number left over. Use the
           leftover words that appear in model names to narrow the list. */
        restTokens = restTokens.filter(function (t) {
          if (STOP[t]) return true;
          var hasNum = /\d/.test(t);
          var keep = out.models.filter(function (m) {
            return tokenize(m.name).some(function (mt) { return mt === t || (hasNum && mt.indexOf(t) === 0); });
          });
          if (!keep.length) return true;
          out.models = keep;
          return false;
        });
        out.models = out.models.slice(0, MAX_RESULTS);
      }
    }

    out.rest = restTokens.filter(function (t) { return !STOP[t]; });
    var d = findDesign(out.rest);
    if (d) { out.design = d.design; out.extra = d.extra; } else { out.extra = out.rest; }

    /* Nothing model-like found: fall back to the v1 whole-text match
       ("ip", "sam", "note") so short typing keeps working. */
    if (!out.models.length && !out.design && tokens.length <= 2) {
      var legacy = search(q);
      if (legacy.length) { out.models = legacy; out.rest = []; out.extra = []; out.modelText = q; }
    }
    return out;
  }

  /* Models that start with / contain the typed fragment */
  function partial(w, max) {
    var hasDigit = /\d/.test(w), hasAlpha = /[a-z]/.test(w);
    var res = [];
    for (var i = 0; i < models.length; i++) {
      var m = models[i], s = -1;
      if (w.length >= 4 && (m.norm.indexOf(w) === 0 || m.normFull.indexOf(w) === 0)) s = 2;
      else if (hasDigit && hasAlpha && w.length >= 3 && m.norm.indexOf(w) > -1) s = 1;
      if (s > -1) { res.push([s, m]); }
    }
    res.sort(function (a, b) { return b[0] - a[0] || a[1].norm.length - b[1].norm.length || b[1].sales - a[1].sales; });
    return res.slice(0, max).map(function (x) { return x[1]; });
  }

  /* Shopify's built-in tag URL: /collections/<handle>/<tag-handle>
     ("Bikes & Cars" -> bikes-cars). No collection has to be created. */
  function tagHandle(tag) {
    return String(tag).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function comboUrl(m, d) {
    return m.url.split('?')[0].replace(/\/+$/, '') + '/' + tagHandle(d.tag);
  }

  function modelTag(m) {
    return m.name.replace(/\+/g, ' Plus').replace(/\s+/g, ' ').trim();
  }

  /* Category rows for a parsed query */
  function categories(p) {
    var rows = [];
    if (p.design && p.models.length) {
      p.models.slice(0, 3).forEach(function (m) {
        rows.push({ title: p.design.label + ' cases for ' + m.name, note: 'Theme', url: comboUrl(m, p.design) });
      });
    }
    var maxModels = (p.design || p.rest.length) ? (p.exact ? 1 : 3) : MAX_RESULTS;
    p.models.slice(0, maxModels).forEach(function (m) {
      rows.push({ title: m.name, note: m.brand, url: m.url });
    });
    if (p.design && p.design.page) {
      rows.push({ title: p.design.label + ' cases', note: 'All models', url: p.design.page });
    }
    return rows;
  }

  /* ---------- products (Shopify predictive search) ---------- */
  var prodCache = {};

  function productQuery(p) {
    var words = p.rest.join(' ');
    if (p.exact && p.models[0]) return { q: (words + ' tag:"' + modelTag(p.models[0]) + '"').trim(), tag: norm(modelTag(p.models[0])) };
    return { q: (words + ' ' + p.modelText).trim(), tag: '', prefix: norm(p.modelText) };
  }

  function shouldFetchProducts(p) {
    return p.rest.length > 0 || !p.models.length;
  }

  function fetchProducts(p, signal) {
    var pq = productQuery(p);
    if (!pq.q) return Promise.resolve([]);
    if (prodCache[pq.q]) return Promise.resolve(prodCache[pq.q]);
    var url = ROOT + 'search/suggest.json?q=' + encodeURIComponent(pq.q) +
      '&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last';
    return fetch(url, { signal: signal, headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var list = (j && j.resources && j.resources.results && j.resources.results.products) || [];
        if (pq.tag) {
          var exact = list.filter(function (x) {
            return (x.tags || []).some(function (t) { return norm(t) === pq.tag; });
          });
          if (exact.length) list = exact;
        } else if (pq.prefix && pq.prefix.length >= 4) {
          /* model typed only partly ("iphone 18"): keep that family */
          var fam = list.filter(function (x) {
            return (x.tags || []).some(function (t) { return norm(t).indexOf(pq.prefix) === 0; });
          });
          if (fam.length) list = fam;
        }
        list = list.slice(0, MAX_PRODUCTS);
        prodCache[pq.q] = list;
        return list;
      });
  }

  function money(v) {
    var n = parseFloat(v);
    if (isNaN(n)) return '';
    return '₹' + (Math.round(n * 100) / 100).toLocaleString('en-IN');
  }

  function thumb(p) {
    var u = (p.featured_image && p.featured_image.url) || p.image || '';
    if (!u) return '';
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=120';
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- styles ---------- */
  function injectStyles() {
    if (document.getElementById('ck-ms-styles')) return;
    var S = '.ck-ms-panel[data-ck-ms] ';
    var INK = 'color:#111827 !important;-webkit-text-fill-color:#111827 !important;';
    var GREY = 'color:#6b7280 !important;-webkit-text-fill-color:#6b7280 !important;';
    var ACC = 'color:var(--ck-accent,#6c5ce7) !important;-webkit-text-fill-color:var(--ck-accent,#6c5ce7) !important;';
    var css = [
      /* While model search is active, the theme's whole-store livesearch
         panel inside the HEADER form stays hidden. If our data fails to
         load, the html class is never added and native search returns. */
      'html.ck-model-search-on [id^="shopify-section-"][id$="__header"] form #livesearch{display:none !important}',
      '.ck-ms-panel{position:absolute;top:calc(100% + 6px);left:0;right:0;background:#fff;border-radius:var(--ck-radius,12px);box-shadow:0 12px 40px rgba(0,0,0,.16);z-index:9999;overflow:hidden;max-height:70vh;overflow-y:auto;text-align:left;-webkit-overflow-scrolling:touch}',
      /* Colours are scoped to [data-ck-ms] with !important because
         async-search.css has `#search div a{color:white}` (see the
         2026-09-26 contrast fix). */
      S + '.ck-ms-head{padding:10px 16px 4px;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;' + GREY + '}',
      S + 'a.ck-ms-item{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:0 16px;min-height:var(--ck-tap,44px);font-size:15px;text-decoration:none !important;' + INK + '}',
      S + 'a.ck-ms-item:hover,' + S + 'a.ck-ms-item.is-active,' + S + 'a.ck-ms-prod:hover,' + S + 'a.ck-ms-prod.is-active{background:rgba(108,92,231,.08)}',
      S + '.ck-ms-name{font-weight:600;' + INK + '}',
      S + '.ck-ms-brand{font-size:11px;text-transform:uppercase;letter-spacing:.05em;flex-shrink:0;' + GREY + '}',
      S + 'a.ck-ms-prod{display:flex;align-items:center;gap:12px;padding:6px 16px;min-height:56px;text-decoration:none !important;' + INK + '}',
      S + '.ck-ms-thumb{width:44px !important;height:44px !important;max-width:44px !important;border-radius:8px;object-fit:contain;background:#f3f4f6;flex-shrink:0;display:block}',
      S + '.ck-ms-ptitle{flex:1;min-width:0;font-size:14px;font-weight:500;line-height:1.3;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;' + INK + '}',
      S + '.ck-ms-price{font-size:14px;font-weight:700;flex-shrink:0;' + INK + '}',
      S + '.ck-ms-empty{padding:14px 16px;font-size:14px;' + GREY + '}',
      S + 'a.ck-ms-all{display:flex;align-items:center;min-height:var(--ck-tap,44px);padding:0 16px;font-weight:700;font-size:14px;text-decoration:none !important;border-top:1px solid #f3f4f6;' + ACC + '}',
      S + 'a.ck-ms-all:hover,' + S + 'a.ck-ms-all.is-active{background:rgba(108,92,231,.08)}',
      /* chips on the /search results page */
      '.ck-ms-chips{display:flex;flex-wrap:wrap;gap:8px;margin:12px 0 16px;align-items:center}',
      '.ck-ms-chips span{font-size:13px;color:#6b7280;margin-right:4px}',
      '.ck-ms-chips a{display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:999px;border:1px solid #e5e7eb;background:#fff;color:#111827;font-size:14px;font-weight:600;text-decoration:none}',
      '.ck-ms-chips a:hover{border-color:var(--ck-accent,#6c5ce7);color:var(--ck-accent,#6c5ce7)}',
      '.ck-ms-chips a.is-primary{background:var(--ck-accent,#6c5ce7);border-color:var(--ck-accent,#6c5ce7);color:#fff}'
    ].join('\n');
    var el = document.createElement('style');
    el.id = 'ck-ms-styles';
    el.textContent = css;
    document.head.appendChild(el);
  }

  /* ---------- typewriter placeholder ---------- */
  function startTyper(input) {
    if (input.__ckTyper) return;
    input.__ckTyper = true;
    if (reduceMotion) {
      input.setAttribute('placeholder', 'Try “' + PHRASES[0] + '”');
      return;
    }
    var pi = 0, ci = 0, deleting = false;
    function tick() {
      if (!document.body.contains(input)) return;
      if (document.activeElement === input || input.value) {
        input.setAttribute('placeholder', STATIC_PLACEHOLDER);
        setTimeout(tick, 1500);
        return;
      }
      var word = PHRASES[pi % PHRASES.length];
      if (!deleting) {
        ci++;
        input.setAttribute('placeholder', 'Try “' + word.slice(0, ci) + '”');
        if (ci >= word.length) {
          deleting = true;
          setTimeout(tick, 1700);
          return;
        }
      } else {
        ci--;
        if (ci <= 0) {
          deleting = false;
          pi++;
          input.setAttribute('placeholder', STATIC_PLACEHOLDER); /* no empty Try "" flash */
        } else {
          input.setAttribute('placeholder', 'Try “' + word.slice(0, ci) + '”');
        }
      }
      setTimeout(tick, deleting ? 30 : 70);
    }
    tick();
  }

  /* ---------- suggestion panel ---------- */
  function enhance(input) {
    if (input.__ckms) return;
    input.__ckms = true;
    var form = input.form;
    if (!form) return;

    if (getComputedStyle(form).position === 'static') form.style.position = 'relative';
    input.setAttribute('autocomplete', 'off');

    var panel = document.createElement('div');
    panel.className = 'ck-ms-panel';
    panel.id = 'ck-ms-panel-' + (++panelCount);
    panel.setAttribute('data-ck-ms', '1');
    panel.hidden = true;
    form.appendChild(panel);
    var active = -1;
    var timer = null;
    var ctrl = null;
    var current = '';

    function close() {
      panel.hidden = true;
      panel.innerHTML = '';
      active = -1;
      current = '';
      if (timer) clearTimeout(timer);
      if (ctrl) ctrl.abort();
    }

    /* products: undefined = still loading, [] = none */
    function draw(q, p, products) {
      var rows = categories(p);
      var html = '';
      if (rows.length) {
        html += '<div class="ck-ms-head">' + (p.design || p.rest.length ? 'Categories' : 'Phone models') + '</div>';
        html += rows.map(function (r) {
          return '<a class="ck-ms-item" data-ck-nav href="' + escapeHtml(r.url) + '">' +
            '<span class="ck-ms-name">' + escapeHtml(r.title) + '</span>' +
            '<span class="ck-ms-brand">' + escapeHtml(r.note) + '</span></a>';
        }).join('');
      }
      if (products && products.length) {
        html += '<div class="ck-ms-head">Products</div>';
        html += products.map(function (x) {
          var t = thumb(x);
          return '<a class="ck-ms-prod" data-ck-nav href="' + escapeHtml(x.url) + '">' +
            (t ? '<img class="ck-ms-thumb" loading="lazy" alt="" src="' + escapeHtml(t) + '">' : '<span class="ck-ms-thumb"></span>') +
            '<span class="ck-ms-ptitle">' + escapeHtml(x.title) + '</span>' +
            '<span class="ck-ms-price">' + money(x.price) + '</span></a>';
        }).join('');
      }
      var loading = products === undefined && shouldFetchProducts(p);
      if (!rows.length && !loading && !(products && products.length)) {
        html += '<div class="ck-ms-empty">No matches for “' + escapeHtml(q) + '”.</div>' +
          '<a class="ck-ms-all" data-ck-nav href="' + BROWSE_ALL_URL + '">Browse all models →</a>';
      } else if (!rows.length && loading) {
        html += '<div class="ck-ms-empty">Searching…</div>';
      } else if (shouldFetchProducts(p)) {
        html += '<a class="ck-ms-all" data-ck-nav href="' + ROOT + 'search?q=' + encodeURIComponent(q) + '">View all results for “' + escapeHtml(q) + '” →</a>';
      }
      panel.innerHTML = html;
      panel.hidden = false;
      active = -1;
    }

    function render(q) {
      var p = parse(q);
      current = q;
      if (timer) clearTimeout(timer);
      if (ctrl) ctrl.abort();
      if (!shouldFetchProducts(p)) { draw(q, p, []); return; }
      var pq = productQuery(p);
      if (prodCache[pq.q]) { draw(q, p, prodCache[pq.q]); return; }
      draw(q, p, undefined);
      timer = setTimeout(function () {
        ctrl = window.AbortController ? new AbortController() : null;
        fetchProducts(p, ctrl ? ctrl.signal : undefined)
          .then(function (list) { if (current === q) draw(q, p, list || []); })
          .catch(function (err) { if (err && err.name === 'AbortError') return; if (current === q) draw(q, p, []); });
      }, 220);
    }

    /* capture + stopPropagation keeps the theme's whole-store
       livesearch from reacting; our panel takes over. */
    input.addEventListener('input', function (e) {
      if (!dataReady) return; /* graceful: native search if data failed */
      e.stopPropagation();
      var v = input.value.trim();
      if (v.length < MIN_CHARS) { close(); return; }
      render(v);
    }, true);

    input.addEventListener('focus', function () {
      if (!dataReady || !panel.hidden) return;
      var v = input.value.trim();
      if (v.length >= MIN_CHARS) render(v);
    });

    input.addEventListener('keydown', function (e) {
      if (panel.hidden) return;
      var links = panel.querySelectorAll('a[data-ck-nav]');
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!links.length) return;
        e.preventDefault();
        e.stopPropagation();
        active += (e.key === 'ArrowDown' ? 1 : -1);
        if (active < 0) active = links.length - 1;
        if (active >= links.length) active = 0;
        links.forEach(function (l, i) { l.classList.toggle('is-active', i === active); });
        if (links[active].scrollIntoView) links[active].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && active > -1 && links[active]) {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = links[active].href;
      } else if (e.key === 'Escape') {
        close();
      }
    }, true);

    form.addEventListener('submit', function (e) {
      if (!dataReady) return;
      var v = input.value.trim();
      if (v.length < MIN_CHARS) return;
      var p = parse(v);
      var dest = '';
      if (p.models.length && !p.rest.length) dest = p.models[0].url;                       /* model only */
      else if (p.exact && p.design && !p.extra.length) dest = comboUrl(p.models[0], p.design); /* model + theme */
      if (dest) {
        e.preventDefault();
        window.location.href = dest;
      }
      /* anything else: native /search proceeds */
    });

    document.addEventListener('click', function (e) {
      if (!form.contains(e.target)) close();
    });

    startTyper(input);
  }

  /* ---------- /search results page: category chips ---------- */
  function searchPageChips() {
    if (!dataReady || document.querySelector('.ck-ms-chips')) return;
    if (!/\/search\/?$/.test(location.pathname)) return;
    var q = new URLSearchParams(location.search).get('q');
    if (!q) return;
    q = q.replace(/\btag:"[^"]*"/g, ' ').trim();
    if (q.length < MIN_CHARS) return;
    var rows = categories(parse(q)).slice(0, 6);
    if (!rows.length) return;
    var sec = document.querySelector('[id^="shopify-section-"][id$="__main-search"]');
    if (!sec) return;
    var anchor = sec.querySelector('.results') || sec.querySelector('#collection');
    if (!anchor) return;
    var bar = document.createElement('div');
    bar.className = 'ck-ms-chips';
    bar.innerHTML = '<span>Shop by category:</span>' + rows.map(function (r, i) {
      return '<a' + (i === 0 ? ' class="is-primary"' : '') + ' href="' + escapeHtml(r.url) + '">' + escapeHtml(r.title) +
        (r.note === 'All models' ? ' · all models' : '') + '</a>';
    }).join('');
    var host = sec.querySelector('form.f8sr') || anchor;
    host.parentNode.insertBefore(bar, host);
  }

  function init() {
    injectStyles();
    if (dataReady) document.documentElement.classList.add('ck-model-search-on');
    var inputs = document.querySelectorAll('[id^="shopify-section-"][id$="__header"] input[name="q"]');
    inputs.forEach(enhance);
    searchPageChips();
  }

  // Start the typewriter + enhancement immediately so the animated placeholder shows from the
  // start (previously it waited for the data fetch, leaving the static theme placeholder up).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);

  // Load model data in the background: enables suggestions + refreshes the phrase list.
  loadData().then(function () {
    if (dataReady) {
      document.documentElement.classList.add('ck-model-search-on');
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', searchPageChips);
      else searchPageChips();
    }
  });

  /* test hook (read-only) */
  window.__ckSearchParse = function (q) { return dataReady ? parse(q) : null; };
})();
